// frontend/src/services/api.ts
/**
 * Pensado por: Alcélio Gomes
 * Codificado em coautoria: Claude Code
 * 🚀 NeuroAgentes
 * 
 * AUDITORIA: Cliente API seguro usando cookies httpOnly e CSRF protection.
 * Inclui interceptors, refresh tokens e tratamento de erros.
 */

import axios from "axios";
import {
  API_CONFIG,
  API_ENDPOINTS,
  addCsrfToken,
  buildApiUrl,
} from "../config/api.config";

/**
 * Cliente API seguro usando cookies httpOnly e CSRF protection
 */
class SecureApiClient {
  private axiosInstance: any;
  private csrfToken: string | null = null;
  private isRefreshing = false;
  private failedQueue: Array<{
    resolve: (token: string) => void;
    reject: (error: any) => void;
  }> = [];

  constructor() {
    this.axiosInstance = axios.create({
      ...API_CONFIG,
      withCredentials: true, // Importante para cookies httpOnly
    });

    this.setupInterceptors();
  }

  private setupInterceptors(): void {
    // Request interceptor para adicionar CSRF token
    this.axiosInstance.interceptors.request.use(
      async (config: any) => {
        await this.ensureCsrfToken(config);
        return config;
      },
      (error: any) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor para lidar com erros de autenticação
    this.axiosInstance.interceptors.response.use(
      (response: any) => response,
      async (error: any) => {
        const originalRequest = error.config;

        // Erro 401 - Token expirado ou inválido
        if (error.response?.status === 401 && !originalRequest._retry) {
          return this.handleUnauthorized(originalRequest, error);
        }

        // Erro 403 - CSRF token inválido ou missing
        if (error.response?.status === 403) {
          return this.handleForbidden(originalRequest, error);
        }

        return Promise.reject(error);
      }
    );
  }

  /**
   * Garante que temos um CSRF token válido
   */
  private async ensureCsrfToken(config: any): Promise<void> {
    // Só precisamos de CSRF token para mutations (POST, PUT, PATCH, DELETE)
    const needsCsrf = ["post", "put", "patch", "delete"].includes(
      config.method?.toLowerCase() || ""
    );

    if (needsCsrf && !this.csrfToken) {
      await this.fetchCsrfToken();
    }

    // Adicionar CSRF token aos headers se necessário
    if (needsCsrf && this.csrfToken) {
      config.headers = addCsrfToken(config.headers || {}, this.csrfToken);
    }
  }

  /**
   * Busca um novo CSRF token do servidor
   */
  private async fetchCsrfToken(): Promise<void> {
    try {
      console.log("🔐 Buscando CSRF token...");

      // Usar uma instância axios limpa para evitar loops
      const cleanAxios = axios.create({
        withCredentials: true,
        timeout: 10000, // 10 segundos de timeout
      });

      const response = await cleanAxios.get(
        buildApiUrl(API_ENDPOINTS.auth.csrfToken)
      );

      this.csrfToken = (response.data as any).csrfToken;

      if (!this.csrfToken) {
        throw new Error("CSRF token não recebido do servidor");
      }

      console.log("✅ CSRF token obtido com sucesso");
    } catch (error) {
      console.error("❌ Erro ao buscar CSRF token:", error);

      // Se for erro de rede, tentar novamente após um delay
      if (
        (error as any).code === "ECONNREFUSED" ||
        (error as any).code === "NETWORK_ERROR"
      ) {
        console.log("🔄 Tentando obter CSRF token novamente após delay...");
        await new Promise((resolve) => setTimeout(resolve, 1000));

        try {
          const retryResponse = await axios.get(
            buildApiUrl(API_ENDPOINTS.auth.csrfToken),
            {
              withCredentials: true,
            }
          );
          this.csrfToken = (retryResponse.data as any).csrfToken;
          console.log("✅ CSRF token obtido na segunda tentativa");
          return;
        } catch (retryError) {
          console.error("❌ Falha na segunda tentativa:", retryError);
        }
      }

      throw new Error("Falha ao obter token de segurança");
    }
  }

  /**
   * Lida com erros 401 (não autorizado)
   */
  private async handleUnauthorized(
    originalRequest: any,
    error: any
  ): Promise<any> {
    // Verificar se estamos em uma página pública
    const isPublicPage = this.isPublicRoute();
    if (isPublicPage) {
      console.log("🔓 Erro 401 em página pública, ignorando");
      return Promise.reject(error);
    }

    // Verificar se já estamos tentando fazer refresh
    if (this.isRefreshing) {
      return new Promise((resolve, reject) => {
        this.failedQueue.push({ resolve, reject });
      }).then(() => {
        return this.axiosInstance(originalRequest);
      });
    }

    originalRequest._retry = true;
    this.isRefreshing = true;

    try {
      console.log("🔄 Tentando renovar token...");
      await this.refreshToken();

      // Processar fila de requisições que falharam
      this.processQueue(null);

      // Retentar requisição original
      return this.axiosInstance(originalRequest);
    } catch (refreshError) {
      console.error("❌ Falha ao renovar token:", refreshError);
      this.processQueue(refreshError);
      this.handleLogout();
      return Promise.reject(error);
    } finally {
      this.isRefreshing = false;
    }
  }

  /**
   * Lida com erros 403 (proibido) - geralmente CSRF
   */
  private async handleForbidden(
    originalRequest: any,
    error: any
  ): Promise<any> {
    // Se for erro de CSRF ou token inválido, tentar obter novo token
    const errorMessage =
      error.response?.data?.error || error.response?.data?.message || "";
    const isCsrfError =
      errorMessage.includes("CSRF") ||
      errorMessage.includes("Token CSRF") ||
      errorMessage.includes("csrf") ||
      error.response?.status === 403;

    if (isCsrfError && !originalRequest._csrfRetry) {
      console.log("🔐 Erro de CSRF detectado, obtendo novo token...");

      originalRequest._csrfRetry = true;

      try {
        // Limpar token atual
        this.csrfToken = null;

        // Obter novo token
        await this.fetchCsrfToken();

        // Adicionar novo CSRF token à requisição original
        if (this.csrfToken) {
          originalRequest.headers = addCsrfToken(
            originalRequest.headers || {},
            this.csrfToken
          );
        }

        console.log("🔄 Tentando novamente com novo CSRF token...");
        // Retentar requisição
        return this.axiosInstance(originalRequest);
      } catch (csrfError) {
        console.error("❌ Falha ao obter novo CSRF token:", csrfError);
        // Se falhou 2 vezes, redirecionar para login
        this.handleLogout();
      }
    }

    return Promise.reject(error);
  }

  /**
   * Processa a fila de requisições que falharam durante refresh
   */
  private processQueue(error: any): void {
    this.failedQueue.forEach(({ resolve, reject }) => {
      if (error) {
        reject(error);
      } else {
        resolve("success");
      }
    });

    this.failedQueue = [];
  }

  /**
   * Verifica se estamos em uma rota pública
   */
  private isPublicRoute(): boolean {
    const publicRoutes = [
      "/login",
      "/register",
      "/register-full",
      "/",
      "/forgot-password",
    ];
    const currentPath = window.location.pathname;
    return publicRoutes.includes(currentPath);
  }

  /**
   * Tenta renovar o token de autenticação
   */
  private async refreshToken(): Promise<void> {
    try {
      const response = await axios.post(
        buildApiUrl(API_ENDPOINTS.auth.refreshToken),
        {},
        {
          withCredentials: true,
          headers: this.csrfToken ? { "X-CSRF-Token": this.csrfToken } : {},
        }
      );

      if ((response.data as any).success) {
        console.log("✅ Token renovado via cookies");
        // Como estamos usando cookies, não precisamos fazer nada aqui
        // O servidor já definiu o novo cookie automaticamente
      } else {
        throw new Error("Resposta de refresh inválida");
      }
    } catch (error) {
      console.error("❌ Erro ao renovar token:", error);
      throw error;
    }
  }

  /**
   * Faz logout e redireciona para login
   */
  private handleLogout(): void {
    console.log("🔓 Fazendo logout automático...");

    // Limpar CSRF token
    this.csrfToken = null;

    // Redirecionar para login
    window.location.href = "/login";
  }

  /**
   * Métodos públicos da API com suporte a TypeScript Generics
   */
  async get<T = any>(
    url: string,
    config?: any
  ): Promise<{ data: T; status: number; statusText: string; headers: any }> {
    return this.axiosInstance.get(url, config);
  }

  async post<T = any>(
    url: string,
    data?: any,
    config?: any
  ): Promise<{ data: T; status: number; statusText: string; headers: any }> {
    return this.axiosInstance.post(url, data, config);
  }

  async put<T = any>(
    url: string,
    data?: any,
    config?: any
  ): Promise<{ data: T; status: number; statusText: string; headers: any }> {
    return this.axiosInstance.put(url, data, config);
  }

  async patch<T = any>(
    url: string,
    data?: any,
    config?: any
  ): Promise<{ data: T; status: number; statusText: string; headers: any }> {
    return this.axiosInstance.patch(url, data, config);
  }

  async delete<T = any>(
    url: string,
    config?: any
  ): Promise<{ data: T; status: number; statusText: string; headers: any }> {
    return this.axiosInstance.delete(url, config);
  }

  /**
   * Força a renovação do CSRF token
   */
  async refreshCsrfToken(): Promise<void> {
    this.csrfToken = null;
    await this.fetchCsrfToken();
  }

  /**
   * Obtém o CSRF token atual
   */
  getCsrfToken(): string | null {
    return this.csrfToken;
  }

  /**
   * Limpa o CSRF token (usado durante logout)
   */
  clearCsrfToken(): void {
    this.csrfToken = null;
  }
}

// Instância singleton
const secureApi = new SecureApiClient();

export default secureApi;
